import CatchError from '@/components/system/CacthError';
import { APP_FIXED_HEADER_HEIGHT } from '@/constants/global';
import {
  isLoggedIn,
  isTemporaryLoginWithoutToken,
  userInfoMetaData,
} from '@/state/userInfo';
import useUserLoginCallbackHandler from '@/hooks/biz/useUserLoginCallbackHandler';

import useTheme from '@/theme/useTheme';
import { Divider, Box } from '@mui/material';
import { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React from 'react';
import { AppHeaderBar, AppMain } from './styles';
import { GLOBAL_MODALS, globalModals } from '@/state/globalSettings';
import { useAppDrawer } from './hooks/useAppDrawer';

import { MainDrawer } from './MainDrawer';
import { WoWSpaceDrawer } from './WoWSpaceDrawer';
// import { AppBarContent } from './AppBarContent';
import { NewAppBarContent as AppBarContent } from './NewAppBarContent'; // ui-v4
import Loading from '@/components/ui/Loading';
import { isWoWSpaceView } from '@/utils/checkRuntime';
import { store } from '@/state/store';
import { usePWAMode } from '@/utils/pwa';

import WoWSpaceHeader from '@/components/pages/wow-landing-page/layout/Header';

const BindEmailModal = dynamic(
  () => import('@/components/system/Auth/BindEmailModal'),
  {
    ssr: false,
    loading: () => <Loading size={'50px'} />,
  },
);
const DrawerHeader = dynamic(() => import('./DrawerHeader'), {
  ssr: false,
});
export interface AppBarProps extends MuiAppBarProps {
  appSidebarOpened?: boolean;
  children?: React.ReactNode;
}

export function AppDrawer(props: AppBarProps) {
  const { children } = props;
  const { theme } = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { pathname } = useRouter();
  const isPWA = usePWAMode();

  // 检查是否需要隐藏侧边导航栏的页面
  const isWoWHomePage = pathname === '/wow';
  const isAuthPage =
    pathname === '/account/login' || pathname === '/account/register';
  const shouldHideSidebar = isWoWHomePage || isAuthPage;

  const { appSidebarOpen, toggleDrawerOpen, handleHeaderIconClick } =
    useAppDrawer(isMobile);
  useUserLoginCallbackHandler();
  const loggedIn = useAtomValue(isLoggedIn);
  const isTemporaryLogin = useAtomValue(isTemporaryLoginWithoutToken);
  const { userMetaData } = useAtomValue(userInfoMetaData);
  const [modals, setModals] = useAtom(globalModals);

  return (
    <>
      {/* 保留应用的默认顶部导航栏 */}
      <AppHeaderBar
        position="fixed"
        open={Boolean(appSidebarOpen)}
        isIndieGameView={isWoWSpaceView}
        className="app-header-bar"
      >
        {!isWoWSpaceView && !shouldHideSidebar && (
          <DrawerHeader
            appSidebarOpen={appSidebarOpen}
            toggleDrawerOpen={toggleDrawerOpen}
            isMobile={isMobile}
          />
        )}
        <AppBarContent
          isMobile={isMobile}
          loggedIn={loggedIn}
          isTemporaryLogin={isTemporaryLogin}
        />
      </AppHeaderBar>

      {/* 如果是 WoW 首页或登录/注册页面，则不显示任何侧边导航栏 */}
      {/* {!shouldHideSidebar &&
        (!isWoWSpaceView ? (
          <MainDrawer appSidebarOpen={appSidebarOpen} isMobile={isMobile} />
        ) : (
          <WoWSpaceDrawer appSidebarOpen={appSidebarOpen} isMobile={isMobile} />
        ))} */}

      {shouldHideSidebar ? null : isWoWSpaceView ? (
        <WoWSpaceDrawer
          appSidebarOpen={appSidebarOpen}
          isMobile={isMobile}
          toggleDrawerOpen={toggleDrawerOpen}
        />
      ) : (
        <MainDrawer appSidebarOpen={appSidebarOpen} isMobile={isMobile} />
      )}

      <AppMain
        open={shouldHideSidebar ? false : appSidebarOpen}
        isIndieGameView={isWoWSpaceView}
        isSportsPage={pathname === '/sports'}
        className="app-main-content"
      >
        <Divider
          className="app-content-divider"
          sx={{
            marginTop: APP_FIXED_HEADER_HEIGHT + 'px',
            borderBottom: 'none',
          }}
        />
        {children}
      </AppMain>

      <BindEmailModal
        thirdPartyLoginType={userMetaData?.thirdPartyLoginType}
        open={modals[GLOBAL_MODALS.BIND_EMAIL]}
        onClose={() => setModals({ [GLOBAL_MODALS.BIND_EMAIL]: false })}
        tgUserString={userMetaData?.thirdPartyAuthCode}
      />
    </>
  );
}

export default CatchError(AppDrawer);
